/** Feedback: the report form behind the sidebar's Feedback item. It files a GitHub issue through the feedback API, with the page it was sent from and any voice notes (#717). */
import { useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { captureClientHints, reportedRoute, submitFeedback, type FeedbackType } from "../../api/feedback";
import { Button, Dialog, Field, SegmentedTabs, Textarea, useToast } from "../kit";
import { FEEDBACK_KINDS, useCommentBox, VOICE_ONLY } from "./feedbackForm";
import { VoiceNotes, type VoiceNotesState } from "./VoiceNotes";

const FORM = "feedback-form";

export function Feedback({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname, search } = useLocation();
  const toast = useToast();
  const [type, setType] = useState<FeedbackType>("bug");
  const { text: description, setText: setDescription, box, addSummary } = useCommentBox();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [voice, setVoice] = useState<VoiceNotesState>({ ids: [], busy: false });
  const ready = (!!description.trim() || voice.ids.length > 0) && !voice.busy && !sending;

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setSending(true);
    setFailed(false);
    try {
      const res = await submitFeedback({ type, description: description.trim() || VOICE_ONLY, voiceNotes: voice.ids, route: reportedRoute(pathname, search), localTime: new Date().toISOString(), clientHints: captureClientHints() });
      toast.show({ message: res.message });
      setDescription("");
      onClose();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Feedback"
      size="md"
      footer={
        <>
          <Button variant="surface" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={FORM} disabled={!ready} aria-busy={sending}>
            Send
          </Button>
        </>
      }
    >
      <form id={FORM} onSubmit={(event) => void send(event)} className="flex flex-col gap-3">
        <SegmentedTabs value={type} onChange={setType} options={FEEDBACK_KINDS} label="Kind" className="w-full" />
        <Field label="What happened" hideLabel error={failed ? "Not sent. Try again." : undefined}>
          <Textarea ref={box} data-autofocus placeholder="What happened" value={description} onChange={(event) => setDescription(event.target.value)} />
        </Field>
        <VoiceNotes onSummary={addSummary} onChange={setVoice} />
      </form>
    </Dialog>
  );
}
